import React, { useCallback, useMemo, useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Calendar, Clock, Mail, Phone, Trash2, Save, Download, User, Plus, ChevronLeft, ChevronRight, List, CalendarDays, LayoutGrid } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["reserved", "pending_payment", "confirmed", "cancelled"];
const STATUS_LABELS = {
  reserved: "Awaiting confirmation",
  pending_payment: "Payment pending",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
};
const statusColor = (s) =>
  s === "confirmed" ? "bg-champagne-gold text-deep-ink"
  : s === "pending_payment" ? "bg-terracotta/20 text-terracotta"
  : s === "cancelled" ? "bg-deep-ink/10 text-deep-ink/50"
  : "bg-muted-sage/30 text-deep-ink";

const dPart = (iso) => (iso || "").slice(0, 10);
const tPart = (iso) => (iso || "").slice(11, 16);
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfWeek = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const AppointmentsManager = ({ bookings, types, onUpdate, onDelete, onCreate }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(new Date());
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({});
  const [creating, setCreating] = useState(false);
  const [nb, setNb] = useState(null);

  const typeName = useCallback(
    (id) => types.find((type) => type.id === id)?.name || id,
    [types],
  );

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    return bookings.filter((b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (!term) return true;
      return [b.reference, b.patient?.name, b.patient?.email, typeName(b.type_id)]
        .some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [bookings, q, filter, typeName]);

  const byDay = useMemo(() => {
    const m = {};
    for (const b of visible) { (m[dPart(b.slot)] ||= []).push(b); }
    for (const k in m) m[k].sort((a, b) => a.slot.localeCompare(b.slot));
    return m;
  }, [visible]);

  const now = Date.now();
  const upcoming = visible.filter((b) => new Date(b.slot).getTime() >= now).sort((a, b) => new Date(a.slot) - new Date(b.slot));
  const past = visible.filter((b) => new Date(b.slot).getTime() < now).sort((a, b) => new Date(b.slot) - new Date(a.slot));

  const open = (b) => { setSel(b); setEdit({ date: dPart(b.slot), time: tPart(b.slot), status: b.status, format: b.format, notes: b.notes || "" }); };

  const save = async () => {
    const patch = {};
    const newSlot = `${edit.date}T${edit.time}:00`;
    if (edit.date && edit.time && newSlot !== sel.slot) patch.slot = newSlot;
    if (edit.status !== sel.status) patch.status = edit.status;
    if (edit.format !== sel.format) patch.format = edit.format;
    if ((edit.notes || "") !== (sel.notes || "")) patch.notes = edit.notes;
    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); return; }
    await onUpdate(sel.id, patch);
    setSel(null);
  };

  const remove = async () => { await onDelete(sel.id); setSel(null); };

  const openCreate = (dateStr) => {
    setNb({
      type_id: types[0]?.id || "", name: "", email: "", phone: "",
      date: dateStr || ymd(new Date()), time: "10:00", format: "in_person",
      status: "confirmed", notes: "",
    });
  };

  const submitCreate = async () => {
    if (!nb.type_id) return toast.error("Choose a consultation type");
    if (!nb.name.trim()) return toast.error("Patient name is required");
    if (!nb.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nb.email)) return toast.error("A valid patient email is required");
    if (!nb.date || !nb.time) return toast.error("Choose a date and time");
    setCreating(true);
    try {
      await onCreate({
        type_id: nb.type_id, slot: `${nb.date}T${nb.time}:00`,
        patient: { name: nb.name.trim(), email: nb.email.trim(), phone: nb.phone.trim() },
        format: nb.format, status: nb.status, notes: nb.notes,
      });
      setNb(null);
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "This time slot is already booked" : "Could not create appointment");
    } finally { setCreating(false); }
  };

  const Row = ({ b }) => (
    <button
      onClick={() => open(b)}
      data-testid={`appt-${b.reference}`}
      className="flex w-full items-center gap-4 border-t border-border/60 px-4 py-3 text-start transition-colors hover:bg-sand/60"
    >
      <div className="w-32 flex-shrink-0">
        <p className="text-sm font-medium text-deep-ink">{fmtDay(b.slot)}</p>
        <p className="flex items-center gap-1 text-xs text-deep-ink/50"><Clock className="h-3 w-3" />{tPart(b.slot)}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-deep-ink">{b.patient?.name || "—"}</p>
        <p className="truncate text-xs text-deep-ink/50">{typeName(b.type_id)}</p>
      </div>
      <span className="hidden text-xs text-deep-ink/50 sm:block">{b.format === "online" ? "Online" : "In person"}</span>
      <Badge className={`${statusColor(b.status)} flex-shrink-0`}>{STATUS_LABELS[b.status] || b.status}</Badge>
      <span className="hidden font-mono text-xs text-deep-ink/40 md:block">{b.reference}</span>
    </button>
  );

  const Section = ({ title, items, testid }) => (
    <div data-testid={testid}>
      <p className="px-4 pb-1 pt-4 text-xs uppercase tracking-editorial text-deep-ink/50">{title} · {items.length}</p>
      {items.map((b) => <Row key={b.id} b={b} />)}
      {items.length === 0 && <p className="px-4 py-4 text-sm text-deep-ink/40">None.</p>}
    </div>
  );

  const Chip = ({ b }) => (
    <button
      onClick={(e) => { e.stopPropagation(); open(b); }}
      data-testid={`cal-appt-${b.reference}`}
      className={`w-full truncate rounded-md px-1.5 py-1 text-start text-[11px] leading-tight ${statusColor(b.status)} ${b.status === "cancelled" ? "line-through" : ""}`}
      title={`${tPart(b.slot)} · ${b.patient?.name} · ${typeName(b.type_id)}`}
    >
      <span className="font-medium">{tPart(b.slot)}</span> {b.patient?.name || "—"}
    </button>
  );

  // ---- Week view ----
  const weekStart = startOfWeek(cursor);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekLabel = `${weekStart.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – ${addDays(weekStart, 6).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`;

  // ---- Month view ----
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(monthStart);
  const monthCells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const todayYmd = ymd(new Date());

  const shift = (dir) => {
    if (view === "week") setCursor(addDays(cursor, dir * 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-deep-ink/40" />
          <Input data-testid="appt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, reference…" className="bg-warm-ivory ps-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 bg-warm-ivory" data-testid="appt-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex overflow-hidden rounded-full border border-border bg-warm-ivory">
          {[["list", List], ["week", CalendarDays], ["month", LayoutGrid]].map(([v, Icon]) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm capitalize transition-colors ${view === v ? "bg-deep-ink text-warm-ivory" : "text-deep-ink/70 hover:bg-sand"}`}>
              <Icon className="h-4 w-4" />{v}
            </button>
          ))}
        </div>
        <Button onClick={() => openCreate()} data-testid="new-appointment" className="gap-2 rounded-full bg-champagne-gold text-deep-ink hover:bg-deep-ink hover:text-warm-ivory">
          <Plus className="h-4 w-4" />New appointment
        </Button>
      </div>

      {/* Calendar navigation */}
      {view !== "list" && (
        <div className="mt-4 flex items-center justify-between rounded-2xl bg-warm-ivory px-4 py-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="cal-prev" className="rounded-full"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="cal-next" className="rounded-full"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" onClick={() => setCursor(new Date())} data-testid="cal-today" className="rounded-full text-sm">Today</Button>
          </div>
          <p className="font-serif text-lg text-deep-ink" data-testid="cal-label">
            {view === "week" ? weekLabel : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
          </p>
        </div>
      )}

      {/* LIST VIEW */}
      {view === "list" && (
        <div className="mt-4 overflow-hidden rounded-3xl bg-warm-ivory pb-2">
          <Section title="Upcoming" items={upcoming} testid="appt-upcoming" />
          <Section title="Past" items={past} testid="appt-past" />
          {bookings.length === 0 && <p className="p-8 text-center text-deep-ink/50">No appointments yet.</p>}
        </div>
      )}

      {/* WEEK VIEW */}
      {view === "week" && (
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-7" data-testid="week-grid">
          {weekDays.map((d) => {
            const key = ymd(d);
            const items = byDay[key] || [];
            const isToday = key === todayYmd;
            return (
              <div key={key} onClick={() => openCreate(key)} className="min-h-[140px] cursor-pointer rounded-2xl bg-warm-ivory p-2 transition-colors hover:bg-sand/60" data-testid={`week-day-${key}`}>
                <div className="mb-2 flex w-full items-center justify-between px-1">
                  <span className="text-xs uppercase tracking-editorial text-deep-ink/50">{WEEK_LABELS[(d.getDay() + 6) % 7]}</span>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-sm ${isToday ? "bg-champagne-gold text-deep-ink" : "text-deep-ink"}`}>{d.getDate()}</span>
                </div>
                <div className="space-y-1">
                  {items.map((b) => <Chip key={b.id} b={b} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {view === "month" && (
        <div className="mt-4 overflow-hidden rounded-3xl bg-warm-ivory p-2" data-testid="month-grid">
          <div className="grid grid-cols-7">
            {WEEK_LABELS.map((l) => <div key={l} className="px-2 py-2 text-center text-xs uppercase tracking-editorial text-deep-ink/50">{l}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((d) => {
              const key = ymd(d);
              const items = byDay[key] || [];
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = key === todayYmd;
              return (
                <div key={key} onClick={() => openCreate(key)} className={`min-h-[92px] cursor-pointer rounded-xl p-1.5 transition-colors hover:ring-1 hover:ring-champagne-gold ${inMonth ? "bg-sand/50" : "bg-sand/20"}`} data-testid={`month-day-${key}`}>
                  <div className="mb-1 flex w-full justify-end">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-champagne-gold text-deep-ink" : inMonth ? "text-deep-ink" : "text-deep-ink/30"}`}>{d.getDate()}</span>
                  </div>
                  <div className="space-y-1">
                    {items.slice(0, 3).map((b) => <Chip key={b.id} b={b} />)}
                    {items.length > 3 && <p className="px-1 text-[10px] text-deep-ink/50">+{items.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DETAIL / EDIT DIALOG */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="appt-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-deep-ink">{typeName(sel.type_id)}</DialogTitle>
                <DialogDescription className="font-mono text-xs text-deep-ink/50">{sel.reference}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="rounded-2xl bg-sand p-4">
                  <p className="flex items-center gap-2 text-sm font-medium text-deep-ink"><User className="h-4 w-4 text-champagne-gold" />{sel.patient?.name}</p>
                  <p className="mt-1 flex items-center gap-2 text-sm text-deep-ink/70"><Mail className="h-3.5 w-3.5" />{sel.patient?.email}</p>
                  {sel.patient?.phone && <p className="mt-1 flex items-center gap-2 text-sm text-deep-ink/70"><Phone className="h-3.5 w-3.5" />{sel.patient.phone}</p>}
                  {sel.concern && <p className="mt-2 text-sm text-deep-ink/70"><span className="text-deep-ink/40">Concern: </span>{sel.concern}</p>}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline">Fee {sel.fee} {sel.currency}</Badge>
                    {sel.deposit > 0 && <Badge variant="outline">Deposit {sel.deposit} {sel.currency}</Badge>}
                    {sel.payment_required && <Badge className="bg-terracotta/15 text-terracotta">Payment required</Badge>}
                    {sel.created_by === "admin" && <Badge className="bg-muted-sage/30 text-deep-ink">Phone / manual</Badge>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 text-sm text-deep-ink/70"><Calendar className="h-3.5 w-3.5" />Date</p>
                    <Input type="date" data-testid="appt-date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className="bg-sand" />
                  </div>
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 text-sm text-deep-ink/70"><Clock className="h-3.5 w-3.5" />Time</p>
                    <Input type="time" data-testid="appt-time" value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })} className="bg-sand" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Status</p>
                    <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                      <SelectTrigger className={`border-0 ${statusColor(edit.status)}`} data-testid="appt-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Format</p>
                    <Select value={edit.format} onValueChange={(v) => setEdit({ ...edit, format: v })}>
                      <SelectTrigger className="bg-sand" data-testid="appt-format"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="in_person">In person</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Internal notes</p>
                  <Textarea data-testid="appt-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Notes for the practice…" className="bg-sand" rows={3} />
                </div>

                <a href={`${API}/bookings/${sel.id}/ics`} className="inline-flex items-center gap-2 text-sm text-champagne-gold hover:underline" data-testid="appt-ics">
                  <Download className="h-4 w-4" />Download calendar invite (.ics)
                </a>
              </div>

              <DialogFooter className="mt-2 flex-row justify-between gap-2 sm:justify-between">
                <Button variant="ghost" onClick={remove} data-testid="appt-delete" className="gap-2 text-terracotta hover:bg-terracotta/10 hover:text-terracotta">
                  <Trash2 className="h-4 w-4" />Delete
                </Button>
                <Button onClick={save} data-testid="appt-save" className="gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                  <Save className="h-4 w-4" />Save changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CREATE (PHONE) BOOKING DIALOG */}
      <Dialog open={!!nb} onOpenChange={(o) => !o && setNb(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="create-dialog">
          {nb && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-deep-ink">New appointment</DialogTitle>
                <DialogDescription className="text-sm text-deep-ink/60">Create a booking manually — e.g. for a phone request. No online payment is required.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Consultation type</p>
                  <Select value={nb.type_id} onValueChange={(v) => setNb({ ...nb, type_id: v })}>
                    <SelectTrigger className="bg-sand" data-testid="create-type"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Patient name</p>
                  <Input data-testid="create-name" value={nb.name} onChange={(e) => setNb({ ...nb, name: e.target.value })} className="bg-sand" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Email</p>
                    <Input type="email" data-testid="create-email" value={nb.email} onChange={(e) => setNb({ ...nb, email: e.target.value })} className="bg-sand" />
                  </div>
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Phone</p>
                    <Input data-testid="create-phone" value={nb.phone} onChange={(e) => setNb({ ...nb, phone: e.target.value })} className="bg-sand" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 text-sm text-deep-ink/70"><Calendar className="h-3.5 w-3.5" />Date</p>
                    <Input type="date" data-testid="create-date" value={nb.date} onChange={(e) => setNb({ ...nb, date: e.target.value })} className="bg-sand" />
                  </div>
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 text-sm text-deep-ink/70"><Clock className="h-3.5 w-3.5" />Time</p>
                    <Input type="time" data-testid="create-time" value={nb.time} onChange={(e) => setNb({ ...nb, time: e.target.value })} className="bg-sand" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Format</p>
                    <Select value={nb.format} onValueChange={(v) => setNb({ ...nb, format: v })}>
                      <SelectTrigger className="bg-sand" data-testid="create-format"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="in_person">In person</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Status</p>
                    <Select value={nb.status} onValueChange={(v) => setNb({ ...nb, status: v })}>
                      <SelectTrigger className={`border-0 ${statusColor(nb.status)}`} data-testid="create-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Internal notes</p>
                  <Textarea data-testid="create-notes" value={nb.notes} onChange={(e) => setNb({ ...nb, notes: e.target.value })} placeholder="e.g. Booked by phone" className="bg-sand" rows={2} />
                </div>
              </div>

              <DialogFooter className="mt-2">
                <Button onClick={submitCreate} disabled={creating} data-testid="create-submit" className="w-full gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                  <Plus className="h-4 w-4" />{creating ? "Creating…" : "Create appointment"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
